import type { Listing } from "@/types/domain";

type UnknownRecord = Record<string, unknown>;

function truthyFlag(value: unknown): boolean {
  return ["1", "yes", "true"].includes(
    String(value ?? "").trim().toLowerCase(),
  );
}

function collectionListingIsSold(listing: Listing): boolean {
  const statuses = [listing.status, listing.managementStatus]
    .map((value) => String(value ?? "").trim().toLowerCase())
    .filter(Boolean);

  return (
    truthyFlag(listing.sold) ||
    truthyFlag(listing.soldOrWithdrawn) ||
    truthyFlag(listing.sold_or_withdrawn) ||
    statuses.includes("sold") ||
    statuses.includes("withdrawn")
  );
}

function record(value: unknown): UnknownRecord | undefined {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : undefined;
}

function parseJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

/**
 * Lambda URLs and API Gateway deployments have returned both a normal JSON
 * payload and a `{ body: "...json..." }` payload over the lifetime of the app.
 */
export function unwrapApiPayload(value: unknown): unknown {
  let current = value;

  for (let depth = 0; depth < 4; depth += 1) {
    if (typeof current === "string") {
      const parsed = parseJson(current);
      if (parsed === current) return current;
      current = parsed;
      continue;
    }

    const currentRecord = record(current);
    if (
      currentRecord &&
      "body" in currentRecord &&
      ("statusCode" in currentRecord || Object.keys(currentRecord).length === 1)
    ) {
      current = currentRecord.body;
      continue;
    }

    return current;
  }

  return current;
}

function valueForKey(value: unknown, key: string): unknown {
  const source = record(unwrapApiPayload(value));
  if (!source) return undefined;
  if (key in source) return source[key];

  const matchingKey = Object.keys(source).find(
    (candidate) => candidate.toLowerCase() === key.toLowerCase(),
  );
  return matchingKey ? source[matchingKey] : undefined;
}

export function listingArray(value: unknown, keys: string[] = []): Listing[] {
  const payload = unwrapApiPayload(value);
  if (Array.isArray(payload)) return payload as Listing[];

  for (const key of keys) {
    const candidate = unwrapApiPayload(valueForKey(payload, key));
    if (Array.isArray(candidate)) return candidate as Listing[];
  }

  return [];
}

export function mergeListingCollections(
  ...collections: Array<Iterable<Listing>>
): Listing[] {
  const merged = new Map<string, Listing>();

  for (const listings of collections) {
    for (const listing of listings) {
      const listingId = String(listing?.listingId || "").trim();
      if (!listingId) continue;
      merged.set(listingId, {
        ...(merged.get(listingId) || {}),
        ...listing,
        listingId,
      } as Listing);
    }
  }

  return [...merged.values()];
}

export function normalizeMyListingsResponse(value: unknown): Listing[] {
  const payload = unwrapApiPayload(value);
  const direct = listingArray(payload, ["listings", "items"]);
  if (direct.length || Array.isArray(payload)) return direct;

  const data = unwrapApiPayload(valueForKey(payload, "data"));
  if (!record(data)) return [];

  const dairy = valueForKey(data, "dairy");
  const tag = (listings: Listing[], managementStatus: string) =>
    listings.map((listing) => ({ managementStatus, ...listing }));

  return mergeListingCollections(
    tag(listingArray(dairy, ["approved"]), "active"),
    tag(listingArray(dairy, ["pending"]), "pending"),
    listingArray(data, ["beef"]),
    listingArray(data, ["sheep"]),
    listingArray(data, ["other"]),
    tag(listingArray(data, ["sold"]), "sold"),
    tag(listingArray(data, ["expired"]), "expired"),
  );
}

export function listingBelongsToOwner(
  listing: Listing,
  ownerId: string,
): boolean {
  const expected = String(ownerId || "").trim();
  if (!expected) return false;

  return [listing.agentId, listing.userId, listing.ownerId, listing.createdBy]
    .map((value) => String(value || "").trim())
    .some((value) => value === expected);
}

export function isRecentSoldListing(
  listing: Listing,
  now = Date.now(),
  maximumAgeDays = 7,
): boolean {
  if (!collectionListingIsSold(listing)) return false;
  if (!listing.sellingDate) return true;

  const soldAt = new Date(String(listing.sellingDate)).getTime();
  if (!Number.isFinite(soldAt)) return false;
  return Math.abs(now - soldAt) <= maximumAgeDays * 86_400_000;
}
